import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isNil } from 'lodash';
import { Grid, TextField, Typography, Chip } from '@material-ui/core';
import { VICTIM_EXPERTISE_FIELDS } from 'containers/data-structure';
import { translate } from 'containers/services/translate';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import CustomAccordion from 'containers/LawyerLegalProceedingsPage/CivilProceedingRecap/CustomAccordion';
import messages from 'containers/ExpertExpertise/FillExpertise/messages';
import expertiseExpertiseIcon from 'images/expert-expertise-icon.svg';
import CustomSelect from 'containers/ExpertExpertise/FillExpertise/CustomSelect';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import Document from 'components/Document';
import { displayErrorText } from 'containers/services/validateForm';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import {
  renderChipLabel,
  renderColor,
} from 'containers/ExpertExpertise/Recap/utils';
import frLocale from 'date-fns/locale/fr';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment from 'moment';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {
  DOCUMENT,
  MISSION_EXPERISE_VICTIM_STATUS,
} from 'containers/data-structure';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { COLORS } from 'constants/design';
import { arrayTranslate } from 'containers/services/translate';
import { StyledGrid } from 'containers/ExpertExpertise/FillExpertise/style';
import { VictimType } from 'containers/ExpertExpertise/Victim/typings';
import { JudgementOnMeritsDoc } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { MISSION_EXPERTISE_VICTIM_TYPE } from 'containers/ExpertExpertise/ConcernedExpertise/constants';
import { DATE_FORMAT, DOCUMENT_TYPES } from 'constants/global';
import DateFnsUtils from '@date-io/date-fns';
import { handleOpenDocument } from 'services/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';

export interface AccountingExpertiseProps {
  intl: IntlShape;
  title: any;
  setFormBody: Function;
  formBody: any;
  victim: VictimType;
  uploadDocument: Function;
  resetUploadDocumentStore: Function;
  setDocumentId: Function;
  uploadedDocument: IUploadedDocument;
  documentId: string;
  documentIdStore: string;
  document: JudgementOnMeritsDoc;
  setDocument: Dispatch<SetStateAction<JudgementOnMeritsDoc>>;
  validateExpertise: any;
  validateDocumentExpertise: any;
  isReadOnly?: boolean;
  listings: any;
  sagis_code: any;
  mission: any;
  setMissionDate: Function;
  missionDate: any;
  requestGetDocByDocumentumId: Function;
}

export const AccountingExpertiseForm: React.FC<AccountingExpertiseProps> = ({
  title,
  setFormBody,
  formBody,
  victim,
  validateExpertise,
  ...props
}): JSX.Element => {
  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);

  const { message } = displayErrorText(
    validateExpertise,
    translateArrayErrorMessages(validateExpertise, true),
  );

  const handleChange = (fieldName: string) => (
    e: React.ChangeEvent<{ value: unknown; name?: string }>,
  ) => {
    setIsInputChanged(false);
    const { value } = e.target;
    setFormBody({
      ...formBody,
      [fieldName as string]:
        typeof value === 'string' ? value.replace(',', '.') : value,
    });
  };

  const handleChangeText = (fieldName: string) => (
    event: ChangeEvent<{ value: unknown }>,
  ) => {
    setIsInputChanged(false);
    setFormBody({ ...formBody, [fieldName]: event.target.value });
  };

  const handleDateChange = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const missionDate = moment(theDate as Date).format(DATE_FORMAT);

    props.setMissionDate(missionDate);
  };

  const setDoc = (document: CorporealDocument) => {
    if (!document.name && !document.type) {
      props.setDocument({
        ...document,
        url: document.url,
        response: document.url,
      } as any);

      props.resetUploadDocumentStore();
    } else if (props.documentId === props.documentIdStore) {
      props.setDocument({
        ...document,
        name: document.name,
        type: document.type,
        url: document.url,
      });
      props.resetUploadDocumentStore();
    }
  };

  const documentError = displayErrorText(
    props.validateDocumentExpertise,
    translateArrayErrorMessages(props.validateDocumentExpertise, true),
  );

  const deleteDoc = () => {
    if (!get(props.document, 'response', null)) {
      props.setDocument({ ...props.document, response: '', url: '' } as any);
    }
    props.setDocument({ ...props.document, url: '' });
  };
  const assignement = get(props.mission, 'assignedExperts').find(
    (element: any) => element.expertCode === props.sagis_code,
  );

  return (
    <CustomAccordion
      title={title}
      textColor={COLORS.ACCORDION_TEXT_BLUE}
      bgColor={COLORS.ACCORDION_BG_BLUE}
      image={expertiseExpertiseIcon}
    >
      <StyledGrid container spacing={3} alignItems="center">
        <Grid item xs={4}>
          <CustomSelect
            title={translate(messages.victim, props)}
            items={[
              { label: `${victim.firstName} ${victim.lastName}`, value: 1 },
            ]}
            value={1}
            disabled
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label={translate(messages.annualFixedRevenue, props)}
            name={VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME}
            value={get(
              formBody,
              VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME,
              '',
            )}
            fullWidth
            error={
              !!get(validateExpertise, [
                VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME,
              ]) &&
              !isInputChanged &&
              !get(formBody, VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME, '')
            }
            helperText={
              !isInputChanged &&
              !get(formBody, VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME, '') &&
              message[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
            }
            InputProps={{
              disabled: props.isReadOnly,
            }}
            type="number"
            onChange={handleChange(VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME)}
          />
        </Grid>
        {isNil(get(props.mission, 'missionDate')) && (
          <Grid item>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                invalidDateMessage=""
                format="dd/MM/yyyy"
                id="lossDate"
                onChange={handleDateChange}
                name={'mission-date'}
                value={dateStringToLocalDate(props.missionDate)}
                label={translate({ ...messages.missionDate }, props)}
                className="input"
                error={props.missionDate === 'Invalid date'}
                helperText={
                  props.missionDate === 'Invalid date'
                    ? translate(messages.invalidDate, props)
                    : ''
                }
              />
            </MuiPickersUtilsProvider>
          </Grid>
        )}
        <Grid item xs={4}>
          <TextField
            title={
              assignement?.type ===
                MISSION_EXPERTISE_VICTIM_TYPE.APPRECIATION &&
              get(formBody, 'createdBy', '') === props.sagis_code
                ? translate(messages.commentAppreciation, props)
                : translate(messages.comment, props)
            }
            label={
              assignement?.type ===
                MISSION_EXPERTISE_VICTIM_TYPE.APPRECIATION &&
              get(formBody, 'createdBy', '') === props.sagis_code
                ? translate(messages.commentAppreciation, props)
                : translate(messages.comment, props)
            }
            value={formBody.comment}
            onChange={handleChangeText('comment')}
            fullWidth
            InputProps={{
              disabled: props.isReadOnly,
            }}
          />
        </Grid>
        {get(formBody, 'status') ===
        MISSION_EXPERISE_VICTIM_STATUS.VALIDATED ? (
          <Grid item xs={12}>
            <section>
              {props.uploadedDocument && (
                <div className="support-document-info">
                  <Document
                    name={get(props.uploadedDocument, 'name', '')}
                    type={get(props.uploadedDocument, 'type', '')}
                    icon={
                      <VisibilityIcon
                        className="consult"
                        onClick={handleOpenDocument(
                          props.uploadedDocument as any,
                          props.requestGetDocByDocumentumId,
                        )}
                      />
                    }
                  />
                </div>
              )}
            </section>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Grid item xs={12}>
              {!props.isReadOnly && (
                <span style={{ fontSize: '13px', color: 'red' }}>
                  {translate({ ...messages.documentStampedTitle }, props)}
                </span>
              )}
              <UploadDocumentBloc
                index={0}
                document={props.document}
                resetUploadedDocument={props.resetUploadDocumentStore}
                uploadDocument={props.uploadDocument}
                uploadedDocument={props.uploadedDocument}
                docType={DOCUMENT_TYPES.RAPPORT_CONSEIL}
                setDocumentId={props.setDocumentId}
                docId={props.documentId}
                setDocument={setDoc}
                deleteDoc={
                  !isNil(get(formBody, 'id')) &&
                  (get(formBody, 'status') ===
                    MISSION_EXPERISE_VICTIM_STATUS.PENDING ||
                    get(formBody, 'status') ===
                      MISSION_EXPERISE_VICTIM_STATUS.VALIDATED)
                    ? undefined
                    : props.sagis_code === get(formBody, 'createdBy')
                    ? deleteDoc
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <div className="error">
                {documentError?.message[DOCUMENT.NAME]}
              </div>
            </Grid>
          </Grid>
        )}
        {((props.isReadOnly && get(formBody, 'status')) ||
          get(formBody, 'status') ===
            MISSION_EXPERISE_VICTIM_STATUS.REJECTED) && (
          <>
            <Grid item>
              <Typography
                style={{
                  color: COLORS.LIGHT_GREY,
                  fontWeight: 400,
                  fontSize: '13px',
                }}
              >
                {translate(messages.statusTitle, props)}
              </Typography>
            </Grid>
            <Grid item xs={11}>
              <Chip
                style={{
                  backgroundColor: renderColor(get(formBody, 'status')),
                  color: COLORS.WHITE,
                  fontWeight: 700,
                }}
                label={renderChipLabel(get(formBody, 'status'))}
              />
            </Grid>
          </>
        )}
        {(props.isReadOnly &&
          get(formBody, 'status') &&
          get(formBody, 'createdBy')) ||
        get(formBody, 'status') === MISSION_EXPERISE_VICTIM_STATUS.REJECTED ? (
          <>
            <Grid item>
              <Typography
                style={{
                  color: COLORS.LIGHT_GREY,
                  fontWeight: 400,
                  fontSize: '13px',
                }}
              >
                {translate(messages.createdByTitle, props)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                style={{
                  color: COLORS.TEXT_BLACK,
                  fontWeight: 400,
                  fontSize: '13px',
                }}
              >
                {getValueByCode(
                  get(formBody, 'createdBy') as string,
                  get(props.listings, 'intervenants'),
                )}
              </Typography>
            </Grid>
          </>
        ) : (
          <></>
        )}
        {get(formBody, 'decisionComment') && (
          <>
            <Grid item>
              <Typography
                style={{
                  color: COLORS.LIGHT_GREY,
                  fontWeight: 400,
                  fontSize: '13px',
                }}
              >
                {translate(messages.comment, props) + ':'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                style={{
                  color: COLORS.TEXT_BLACK,
                  fontWeight: 400,
                  fontSize: '13px',
                }}
              >
                {get(formBody, 'decisionComment')}
              </Typography>
            </Grid>
          </>
        )}
        {get(formBody, 'rejectionReason') &&
          get(formBody, 'status') ===
            MISSION_EXPERISE_VICTIM_STATUS.REJECTED && (
            <>
              <Grid item>
                <Typography
                  style={{
                    color: COLORS.LIGHT_GREY,
                    fontWeight: 400,
                    fontSize: '13px',
                  }}
                >
                  {translate(messages.reason, props) + ':'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  style={{
                    color: COLORS.TEXT_BLACK,
                    fontWeight: 400,
                    fontSize: '13px',
                  }}
                >
                  {get(formBody, 'rejectionReason')}
                </Typography>
              </Grid>
            </>
          )}
      </StyledGrid>
    </CustomAccordion>
  );
};

export default injectIntl(AccountingExpertiseForm);
